// "My queue" — the sales user's assigned requests. Ledger-style: one ruled
// table, no card-per-row, no sidebar (root CLAUDE.md). Backend now joins
// `organizations.name` into `listAssignedTo`'s response (see
// backend/src/repositories/requestRepository.js) — the numeric
// `workspace.organizationFallback` string stays as a defensive fallback
// for the unlikely case that field is ever absent, not the primary path.
//
// Reassign column (can_assign permission, utils/permissionCatalog.js):
// only rendered when the backend's own `canAssign` flag is true — the real
// gate is server-side (POST /sales/requests/:requestId/assign 403s without
// the permission), this flag only decides whether to show the control at
// all (admin/CLAUDE.md: "Role gating is enforced by the API, not by hiding
// UI").

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useSalesQueue,
  useAssignableSalesUsers,
  useAssignRequest,
} from '../hooks/useSalesQueue.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { statusLabel } from '../i18n.js';
import { formatDate } from '../utils/formatDate.js';
import { useSortableList } from 'shared/hooks/useSortableList.js';
import { SortHeader } from 'shared/components/SortHeader/index.js';
import styles from './QueuePage.module.css';

// key: the row field to sort on; numeric: compare as numbers, not strings —
// same shape as AllProformasPage.jsx's own SORT_COLUMNS. `created` has no
// own displayed column (same as AllProformasPage's) — it's the default sort
// only, mirroring the server's own default order (requestRepository.js#
// listAssignedTo: created_at DESC, newest-assigned-first, entry 130).
const SORT_COLUMNS = {
  ref: { key: 'ref_no' },
  organization: { key: 'organization_name' },
  status: { key: 'status' },
  neededBy: { key: 'needed_by' },
  assignedAt: { key: 'assigned_at' },
  created: { key: 'created_at' },
};

function ReassignCell({ request, salesUsers }) {
  const { t, confirm, toastOk, toastErr } = useActionFeedback();
  const assignMutation = useAssignRequest();
  const [selected, setSelected] = useState('');

  const others = (salesUsers || []).filter((u) => u.id !== request.assigned_to);

  const handleChange = async (e) => {
    const assignedTo = Number(e.target.value);
    const target = others.find((u) => u.id === assignedTo);
    setSelected(e.target.value);
    if (!target) return;

    const ok = await confirm({
      title: t('queue.assign.confirmTitle'),
      message: t('queue.assign.confirmBody'),
      confirmLabel: t('queue.assign.button'),
      tone: 'danger',
    });
    setSelected('');
    if (!ok) return;

    assignMutation.mutate(
      { requestId: request.id, assignedTo },
      {
        onSuccess: () => toastOk(t('queue.assign.successToast')),
        onError: (err) =>
          toastErr(err, [
            'ASSIGN_NOT_PERMITTED',
            'INVALID_ASSIGNEE',
            'REQUEST_NOT_ASSIGNABLE',
            'REQUEST_NOT_FOUND',
          ]),
      }
    );
  };

  return (
    <select
      className={styles.assignSelect}
      value={selected}
      disabled={assignMutation.isPending}
      onClick={(e) => e.stopPropagation()}
      onChange={handleChange}
    >
      <option value="" disabled>
        {assignMutation.isPending ? '…' : t('queue.assign.selectPlaceholder')}
      </option>
      {others.map((u) => (
        <option key={u.id} value={u.id}>
          {u.full_name}
        </option>
      ))}
    </select>
  );
}

export default function QueuePage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useSalesQueue();
  const requests = data?.requests;
  const canAssign = Boolean(data?.canAssign);
  const { data: salesUsers } = useAssignableSalesUsers(canAssign);
  const { sortCol, sortDir, handleSort, sortRows } = useSortableList(
    SORT_COLUMNS,
    'created',
    'desc'
  );
  const rows = sortRows(requests || []);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('queue.title')}</h1>
      <p className={styles.subtitle}>{t('queue.subtitle')}</p>

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="queue.errorBody" error={error} onRetry={refetch} />
      )}

      {!isLoading && !isError && requests && requests.length === 0 && (
        <EmptyState titleId="queue.emptyTitle" bodyId="queue.emptyBody" />
      )}

      {!isLoading && !isError && requests && requests.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <SortHeader
                column="ref"
                label={t('queue.col.ref')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="organization"
                label={t('queue.col.organization')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="status"
                label={t('queue.col.status')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="neededBy"
                label={t('queue.col.neededBy')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="assignedAt"
                label={t('queue.col.assignedAt')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              {canAssign && <th>{t('queue.assign.button')}</th>}
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {rows.map((request) => (
              <tr
                key={request.id}
                className={styles.row}
                tabIndex={0}
                role="link"
                onClick={() => navigate(`/requests/${request.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/requests/${request.id}`);
                }}
              >
                <td className={`${styles.refCell} tabularNums`}>{request.ref_no}</td>
                <td>
                  {request.organization_name ??
                    t('workspace.organizationFallback', { id: request.organization_id })}
                </td>
                <td>{statusLabel(request.status, lang)}</td>
                <td>{formatDate(request.needed_by, lang) || t('queue.neededByNone')}</td>
                <td>{formatDate(request.assigned_at, lang) || '—'}</td>
                {canAssign && (
                  <td>
                    <ReassignCell request={request} salesUsers={salesUsers} />
                  </td>
                )}
                <td className={styles.openCell}>{t('queue.open')} →</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
